import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/*
 Lightweight guided product tour.
 Steps reference a `data-tour="<key>"` attribute on a live DOM element.
 If no target found, the step is rendered centered (modal-style).
*/

const STEPS = [
  {
    key: "welcome",
    target: null,
    title: "Welcome to FORM C.",
    body: "A 40-second tour of the four things you'll use daily. You can skip anytime.",
    accent: "Say hello",
  },
  {
    key: "tour-sidebar",
    target: "sidebar",
    title: "Your workspace, one rail.",
    body: "Dashboard, clients, requests, approvals, invoices and the audit log - all a keystroke away.",
    accent: "01 · Navigate",
    placement: "right",
  },
  {
    key: "tour-command",
    target: "command",
    title: "Command anything.",
    body: "Press ⌘K (or Ctrl-K on Windows) from anywhere to jump straight to a page or trigger a quick action.",
    accent: "02 · Speed",
    placement: "right",
  },
  {
    key: "tour-clients",
    target: "clients-nav",
    title: "Clients, the source of truth.",
    body: "Every partner, structure and fund lives here. Open one to see the full 360 - requests, invoices, compliance and more.",
    accent: "03 · Directory",
    placement: "right",
  },
  {
    key: "tour-requests",
    target: "requests-nav",
    title: "Kanban that ties it all together.",
    body: "Log a client request, move it across the board, and hit \u201cMake payment request\u201d - an approved request becomes a sales invoice in one click.",
    accent: "04 · Flow",
    placement: "right",
  },
  {
    key: "tour-approvals",
    target: "approvals-nav",
    title: "Approvals with a paper trail.",
    body: "Directors, compliance and heads sign off from a single inbox. Every decision is captured in the immutable audit log.",
    accent: "05 · Sign-off",
    placement: "right",
  },
  {
    key: "done",
    target: null,
    title: "You're set.",
    body: "Add your first client to unlock the full flow - or explore around, nothing here bites.",
    accent: "Ready",
    cta: { label: "Add a client", to: "/app/clients?new=1" },
  },
];

const TOUR_KEY = "formc_tour_v1_done";

function useRect(target, deps = []) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!target) { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (!el) { setRect(null); return; }
    const compute = () => setRect(el.getBoundingClientRect());
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [target, ...deps]);
  return rect;
}

export default function ProductTour({ forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const { user, checkAuth } = useAuth();

  useEffect(() => {
    if (forceOpen) { setOpen(true); setI(0); return; }
    // Skip if either the browser or the user profile says it's already seen.
    const localDone = localStorage.getItem(TOUR_KEY);
    const serverDone = !!user?.preferences?.tour_seen;
    if (!localDone && !serverDone) {
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
    // Backfill localStorage from server preference so the check is instant next time
    if (serverDone && !localDone) localStorage.setItem(TOUR_KEY, "1");
  }, [forceOpen, user?.preferences?.tour_seen]);

  const step = STEPS[i];
  const rect = useRect(step?.target, [i, open]);

  const finish = useCallback((remember = true) => {
    if (remember) {
      localStorage.setItem(TOUR_KEY, "1");
      // Best-effort - don't block UI if API fails
      api.patch("/auth/me/preferences", { tour_seen: true }).then(() => checkAuth?.()).catch(() => {});
    }
    setOpen(false);
    setI(0);
    onClose?.();
  }, [onClose, checkAuth]);

  if (!open || !step) return null;

  const PAD = 8;
  const spot = rect ? {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  } : null;

  // Tooltip position
  let tooltipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (rect && step.placement === "right") {
    const top = Math.max(24, Math.min(window.innerHeight - 260, rect.top));
    tooltipStyle = { top: `${top}px`, left: `${rect.right + 24}px`, transform: "none" };
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="tour-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] pointer-events-none"
        data-testid="product-tour"
      >
        {/* Dim overlay with cut-out spotlight via SVG mask */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ mixBlendMode: "normal" }}>
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              {spot && (
                <rect
                  x={spot.left} y={spot.top}
                  width={spot.width} height={spot.height}
                  rx={12} ry={12} fill="black"
                />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(6, 12, 30, 0.72)" mask="url(#tour-mask)" />
        </svg>

        {/* Spotlight ring */}
        {spot && (
          <motion.div
            key={step.key + "-ring"}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="absolute rounded-xl ring-2 ring-[#5B8CFF] pointer-events-none"
            style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height, boxShadow: "0 0 0 6px rgba(232,109,60,0.15)" }}
          />
        )}

        {/* Tooltip card */}
        <motion.div
          key={step.key + "-card"}
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 280, damping: 26 }}
          className="absolute w-[360px] max-w-[90vw] bg-white border border-hairline rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] overflow-hidden pointer-events-auto"
          style={tooltipStyle}
          data-testid={`tour-step-${step.key}`}
        >
          <div className="flex items-center justify-between px-5 pt-4">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-accent-amber">
              <Sparkles className="w-3 h-3" /> {step.accent}
            </div>
            <button onClick={() => finish(true)} data-testid="tour-skip" className="text-ink-3 hover:text-ink" aria-label="Skip tour">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-5 pt-3 pb-5">
            <h3 className="font-display text-xl text-ink tracking-tight leading-snug">{step.title}</h3>
            <p className="text-sm text-ink-2 mt-2 leading-relaxed">{step.body}</p>

            {/* Step dots */}
            <div className="flex items-center gap-1.5 mt-5">
              {STEPS.map((s, idx) => (
                <button key={s.key} onClick={() => setI(idx)}
                  aria-label={`Go to step ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-signal" : "w-1.5 bg-ink-3/25 hover:bg-ink-3/50"}`}
                />
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between">
              <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0}
                data-testid="tour-prev"
                className="inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              {i < STEPS.length - 1 ? (
                <button onClick={() => setI(i + 1)} data-testid="tour-next"
                  className="group inline-flex items-center gap-1.5 bg-signal text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-ink transition-colors">
                  Next <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => finish(true)} data-testid="tour-finish"
                    className="text-sm text-ink-2 hover:text-ink px-2 py-2">Explore</button>
                  {step.cta && (
                    <a href={step.cta.to} onClick={() => finish(true)} data-testid="tour-cta"
                      className="inline-flex items-center gap-1.5 bg-accent-amber text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-signal transition-colors">
                      {step.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
